import { $getRoot, type LexicalEditor } from "lexical";
import { useCallback, useState } from "react";
import { Point } from "./point";
import { Rect } from "./rect";

const DIRECTIONS = {
  Downward: 1,
  Upward: -1,
  Indeterminate: 0,
};

function getCurrentIndex(keysLength: number, prevIndex: number): number {
  if (keysLength === 0) return Infinity;
  if (prevIndex >= 0 && prevIndex < keysLength) return prevIndex;

  return Math.floor(keysLength / 2);
}

function getTopLevelNodeKeys(editor: LexicalEditor): string[] {
  return editor.getEditorState().read(() => $getRoot().getChildrenKeys());
}

export function useGetBlockElement(): (
  anchorElem: HTMLElement,
  editor: LexicalEditor,
  event: MouseEvent,
) => HTMLElement | null {
  const [prevIndex, setPrevIndex] = useState(Infinity);

  return useCallback(
    (anchorElem, editor, event) => {
      const anchorElementRect = anchorElem.getBoundingClientRect();
      const topLevelNodeKeys = getTopLevelNodeKeys(editor);

      const blockElem = editor.getEditorState().read(() => {
        let index = getCurrentIndex(topLevelNodeKeys.length, prevIndex);
        let direction = DIRECTIONS.Indeterminate;

        while (index >= 0 && index < topLevelNodeKeys.length) {
          const key = topLevelNodeKeys[index];
          if (typeof key === "undefined") {
            break;
          }
          const elem = editor.getElementByKey(key);
          if (elem === null) {
            break;
          }
          const point = new Point(event.x, event.y);
          const domRect = Rect.fromDOM(elem);
          const { marginTop, marginBottom } = window.getComputedStyle(elem);

          const rect = domRect.generateNewRect({
            bottom: domRect.bottom + parseFloat(marginBottom),
            left: anchorElementRect.left,
            right: anchorElementRect.right,
            top: domRect.top - parseFloat(marginTop),
          });

          const {
            result,
            reason: { isOnTopSide, isOnBottomSide },
          } = rect.contains(point);

          if (result) {
            setPrevIndex(index);
            return elem;
          }

          if (direction === DIRECTIONS.Indeterminate) {
            if (isOnTopSide) {
              direction = DIRECTIONS.Upward;
            } else if (isOnBottomSide) {
              direction = DIRECTIONS.Downward;
            } else {
              // stop search block element
              direction = Infinity;
            }
          }

          index += direction;
        }

        return null;
      });

      return blockElem;
    },
    [prevIndex],
  );
}
